import * as React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Stack, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// --- Custom Hooks & Utils ---
import { getApiBaseUrl } from "./utils/apiUtils";
import { useAuthData } from "./hooks/useAuthData";
import { useSimulationData } from "./hooks/useSimulationData";

// --- Components ---
import AppTitle from "./components/AppTitle/AppTitle";
import Dashboard from "./components/complex/Dashboard";

// --- Pages (Keep your existing imports) ---
import DashboardHome from "./pages/dashboard/DashboardHome";
import Signup from "./pages/Signup";
import LoginPage from "./pages/Login/Login";
import LogoutPage from "./pages/Logout";
import ResetPassword from "./components/Account/ResetPassword/ResetPassword";
import { Info } from "./pages/Info";
import { LanderPage } from "./pages/Lander";
import { Error404 } from "./pages/Error404";
import DashboardTrainingPrograms from "./pages/dashboard/MyPrograms";
import DashboardProgram from "./pages/dashboard/Program";
import DashboardLeaderBoard from "./pages/dashboard/LeaderBoard";
import DashboardSimulations from "./pages/dashboard/Simulations";
import DashboardTeam from "./pages/dashboard/Team";
import DashboardSettings from "./pages/dashboard/Settings";
import DashboardSimulationFeedback from "./pages/dashboard/SimulationFeedback";
import TestAudioVisualizer from "./pages/TestAudio";
import { ExternalScenario } from "./pages/externalscenario/ExternalScenario";
import { ExternalScenarioDebrief } from "./pages/externalscenario/ExternalScenarioDebrief";
import { ExternalScenarioFeedback } from "./pages/externalscenario/ExternalScenarioFeedback";
import ScenarioDetails from "./pages/dashboard/ScenarioDetails";
import LiveKitTest from "./pages/LiveKitTest";
import FeedbackCoach from "./components/FeedbackCoach/FeedbackCoach";
import Account from "./components/Account/Account";
import Profile from "./components/Account/Profile/Profile";
import SignUpInvitedUser from "./components/Account/SignUpInvitedUser/SignUpInvitedUser";
import TwoFA from "./components/Account/TwoFA/TwoFA";

// --- Helpers ---
import { fetchProgram } from "./components/helpers/FetchProgram";
import { fetchScenario } from "./components/helpers/FetchScenario";
import { fetchProgramFromScenarioId } from "./components/helpers/FetchProgramFromScenarioId";
import "./App.css";
import DefaultButton from "./components/simple/DefaultButton";

export const BrandContext = React.createContext(null);

function App() {
  const [brand, setBrand] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // UI State
  const [sideMenuOpen, setSideMenuOpen] = React.useState(true);
  const [showFooter, setShowFooter] = React.useState(true);
  const [showCrumbs, setShowCrumbs] = React.useState(true);

  // 2FA State

  const [partialAuthToken, setPartialAuthToken] = React.useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // 1. Get Base URL
  const api_base_url = React.useMemo(() => getApiBaseUrl(), []);

  // 2. Initialize Brand & Window Listeners
  React.useEffect(() => {
    setBrand({ api_base_url, name: "GuruNow" });
    document.title = "GuruNow";
    setLoading(false);

    let lastWidth = window.innerWidth;

    const handleResize = () => {
      const currentWidth = window.innerWidth;

      // Bail if width didn't change
      if (currentWidth === lastWidth) return;

      lastWidth = currentWidth;

      if (currentWidth < 1080) setSideMenuOpen(false);
      else setSideMenuOpen(true);
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [api_base_url]);

  // 3. Gate API calls on tab visibility
  const [apiReady, setApiReady] = React.useState(
    () => document.visibilityState === "visible"
  );

  React.useEffect(() => {
    function handleVisibilityChange() {
      setApiReady(document.visibilityState === "visible");
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // 4. Call Custom Hooks (must be at top-level; hooks internally respect apiReady)
  const {
    authToken,
    saveAuthToken,
    loggedInUser,
    trainingProgram,
    allTrainingPrograms,
    fetchUser,
  } = useAuthData(api_base_url, apiReady);

  const {
    simulationsData,
    refreshSimulations,
    fetchRecentSimulationsForScenario,
  } = useSimulationData(api_base_url, authToken, apiReady);

  // 4. Memoize Context Value
  const contextValue = React.useMemo(
    () => ({
      brand,
      authToken,
      saveAuthToken,
      trainingProgram,
      allTrainingPrograms,
      user: loggedInUser,
      sideMenuOpen,
      setSideMenuOpen,
      showFooter,
      setShowFooter,
      showCrumbs,
      setShowCrumbs,
      setLoading,
      simulations: simulationsData,
      refreshSimulations,
      fetchRecentSimulationsForScenario,
      isMobile,
      partialAuthToken,
      setPartialAuthToken,
      fetchUser,
    }),
    [
      brand,
      authToken,
      saveAuthToken,
      trainingProgram,
      allTrainingPrograms,
      loggedInUser,
      sideMenuOpen,
      showFooter,
      showCrumbs,
      simulationsData,
      refreshSimulations,
      fetchRecentSimulationsForScenario,
      isMobile,
    ]
  );

  // 5. Memoize Router Configuration
  const router = React.useMemo(() => {
    if (loading) return null;

    return createBrowserRouter([
      {
        path: "/",
        element: <Dashboard />,
        children: [{ index: true, element: <DashboardHome /> }],
      },
      // ... External Scenarios ...
      {
        path: "/externalscenario",
        children: [
          {
            path: ":scenario_id",
            element: <ExternalScenario />,
            handle: { crumb: () => "External Scenario" },
          },
        ],
      },
      {
        path: "/externalscenario_debrief/:simulation_id",
        element: <ExternalScenarioDebrief />,
        handle: { crumb: () => "Debrief" },
      },
      {
        path: "/externalscenario_feedback/:simulation_id",
        element: <ExternalScenarioFeedback />,
        handle: { crumb: () => "Feedback" },
      },
      // ... General Pages ...
      {
        path: "/info/:pagename",
        element: <Info />,
        handle: { crumb: ({ params }) => params.pagename },
      },
      { path: "/lander/:pagename", element: <LanderPage /> },
      { path: "/testaudio", element: <TestAudioVisualizer /> },
      { path: "/404", element: <Error404 /> },

      // ... Auth ...
      { path: "/signup", element: <Signup /> },
      { path: "/signup/:signupFlowName", element: <Signup /> },
      {
        path: "/login",
        element: (
          <>
            <AppTitle title="Login" />
            <LoginPage />
          </>
        ),
      },
      {
        path: "/reset_password",
        element: (
          <>
            <AppTitle title="Reset Password" />
            <ResetPassword />
          </>
        ),
      },
      {
        path: "/signup/invite/:token",
        element: <SignUpInvitedUser />,
      },
      {
        path: "/2fa_init/:token",
        element: <TwoFA isSetup={true} />,
      },
      {
        path: "/2fa_verify/:token",
        element: <TwoFA isSetup={false} />,
      },
      {
        path: "/2fa_verify",
        element: <TwoFA isSetup={false} />,
      },
      { path: "/logout", element: <LogoutPage /> },
      { path: "/livekit/:scenario_id", element: <LiveKitTest /> },
      {
        path: "/user/",
        element: <Account />,
        handle: { crumb: () => "Account" },
        children: [
          {
            path: "settings",
            index: true,
            element: (
              <>
                <AppTitle title="Profile" />
                <Profile />
              </>
            ),
            handle: { crumb: () => "Profile" },
          },
          {
            path: "admin",
            element: null,
            handle: { crumb: () => "Team Management" },
          },
        ],
      },
      {
        path: "version",
        element: (
          <div>
            Commit id:{" "}
            {import.meta.env.VITE_GIT_COMMIT
              ? import.meta.env.VITE_GIT_COMMIT
              : "N/A"}
          </div>
        ),
      },

      // ... Dashboard ...
      {
        path: "/dashboard",
        element: <Dashboard />,
        handle: { crumb: () => "Dashboard" },
        children: [
          { index: true, element: <DashboardHome /> },
          {
            path: "home",
            element: <DashboardHome />,
            handle: { crumb: () => "Home" },
          },
          {
            path: "simulations",
            element: <DashboardSimulations />,
            handle: { crumb: () => "Performance Spotlight" },
          },
          {
            path: "trainingprograms",
            element: <DashboardTrainingPrograms />,
            handle: { crumb: () => "Training Programs" },
          },
          {
            path: "team",
            element: (
              <>
                <AppTitle title="My Team" />
                <DashboardTeam />
              </>
            ),
            handle: { crumb: () => "My Team" },
          },
          {
            path: "leaderboard",
            element: <DashboardLeaderBoard />,
            handle: { crumb: () => "Leaderboard" },
          },
          {
            path: "settings",
            element: <DashboardSettings />,
            handle: { crumb: () => "Settings" },
          },
          {
            path: "trainingprogram/:trainingprogram_id",
            element: (
              <>
                <AppTitle title="My Programs" />
                <DashboardProgram />
              </>
            ),
            loader: async ({ params }) => {
              let program;
              
              // Check if program is in cached allTrainingPrograms
              if (contextValue.allTrainingPrograms && Array.isArray(contextValue.allTrainingPrograms)) {
                program = contextValue.allTrainingPrograms.find(
                  p => p.id === params.trainingprogram_id || p.id === parseInt(params.trainingprogram_id)
                );
              }
              
              // If not cached, fetch it
              if (!program) {
                program = await fetchProgram(
                  { api_base_url },
                  authToken,
                  params.trainingprogram_id
                );
              }
              
              return {
                programName: program.name ?? "Program",
                programUrl: `/dashboard/trainingprogram/${program?.id}`,
              };
            },
            handle: {
              crumb: (match, data) => [
                { label: "My Programs", to: "/dashboard/trainingprograms" },
                { label: data.programName, to: data.programUrl },
              ],
            },
          },
          {
            path: "simulate/:scenario_id",
            element: <ScenarioDetails />,
            loader: async ({ params }) => {
              const scenario = await fetchScenario(
                { api_base_url },
                authToken,
                params.scenario_id
              );
              const program = await fetchProgramFromScenarioId(
                { api_base_url },
                authToken,
                params.scenario_id
              );

              return {
                programName: program.name ?? "Program",
                programUrl: `/dashboard/trainingprogram/${program.id}`,
                scenarioName: scenario.name ?? "Scenario",
                scenarioUrl: `/dashboard/simulate/${scenario?.id}`,
              };
            },
            handle: {
              crumb: (match, data) => [
                {
                  label: "Training Programs",
                  to: "/dashboard/trainingprograms",
                },
                { label: data.programName, to: data.programUrl },
                { label: data.scenarioName, to: data.scenarioUrl },
              ],
            },
          },
          {
            path: "feedback/:simulation_id",
            element: <FeedbackCoach />,
            handle: {
              crumb: () => [
                {
                  label: "Performance Spotlight",
                  to: "/dashboard/simulations",
                },
                { label: "Feedback", to: "/" },
              ],
            },
          },
        ],
      },
    ]);
  }, [loading, authToken, api_base_url, allTrainingPrograms]);

  console.log("app loading", loading)

  if (loading) {
    return (
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ width: 1, height: "100vh" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <React.StrictMode>
      <BrandContext.Provider value={contextValue}>
        <RouterProvider router={router} />
      </BrandContext.Provider>
    </React.StrictMode>
  );
}

export default App;
